/**
 * PDF 转化补点（51 只报事件名；详情进本地 props）：
 * - auth.sdk_ready / auth.click_open：登录窗就绪与点击耗时
 * - deposit.success_first / deposit.recover；trade.success_first
 * - onboarding.*：登录后盘口引导 + 试玩盘 guide 完成/跳过
 *
 * visit.engaged_action 见 analytics-visit-engaged.js（由 store 动态挂接）。
 */
import { getUserId } from '@/utils/auth-token.js';
import {
  trackAnalyticsLog,
  withAcquisitionProps,
  withDepositFunnelValue,
  withTradeFunnelValue,
} from '@/utils/analytics-track';

const AUTH_SDK_READY_STORAGE = 'deepsage-analytics-auth-sdk-ready';
const DEPOSIT_FIRST_PREFIX = 'ds-analytics-deposit-first:';
const TRADE_FIRST_PREFIX = 'ds-analytics-trade-first:';
const DEPOSIT_FAIL_AT_PREFIX = 'ds-analytics-deposit-fail-at:';
const GUIDE_OUTCOME_PREFIX = 'ds-analytics-guide-outcome:';
const RECOVER_WINDOW_MS = 24 * 60 * 60 * 1000;

let authOpenClickAt = 0;
let authClickOpenTrackedForClick = false;

function sessionFlag(key) {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return true;
  }
}

function markSessionFlag(key) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

function userStorageKey(prefix, userId) {
  const uid = String(userId ?? getUserId() ?? '').trim();
  return uid ? `${prefix}${uid}` : `${prefix}anon`;
}

function localFlag(key) {
  if (typeof localStorage === 'undefined') return true;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return true;
  }
}

function markLocalFlag(key) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

/** 用户点击打开登录/注册前调用 */
export function noteAuthOpenClick() {
  authOpenClickAt = Date.now();
  authClickOpenTrackedForClick = false;
}

/**
 * 登录窗真正打开（可交互）时调用。
 * @param {{ source?: string, contextKey?: string }} [meta]
 */
export function noteAuthDialogOpened(meta = {}) {
  if (!sessionFlag(AUTH_SDK_READY_STORAGE)) {
    markSessionFlag(AUTH_SDK_READY_STORAGE);
    trackAnalyticsLog({
      key: 'auth.sdk_ready',
      label: '登录窗就绪',
      value: withAcquisitionProps({
        source: meta.source || undefined,
        contextKey: meta.contextKey || undefined,
      }),
    });
  }

  if (!authOpenClickAt || authClickOpenTrackedForClick) return;
  authClickOpenTrackedForClick = true;
  const durationMs = Math.max(0, Date.now() - authOpenClickAt);
  authOpenClickAt = 0;
  trackAnalyticsLog({
    key: 'auth.click_open',
    label: '点击打开登录耗时',
    value: withAcquisitionProps({
      durationMs,
      source: meta.source || undefined,
      contextKey: meta.contextKey || undefined,
    }),
    durationMs,
  });
}

/**
 * @param {Record<string, unknown>} [value]
 */
export function tryTrackDepositSuccessFirst(value = {}) {
  const key = userStorageKey(DEPOSIT_FIRST_PREFIX);
  if (localFlag(key)) return;
  markLocalFlag(key);
  trackAnalyticsLog({
    key: 'deposit.success_first',
    label: '首次充值成功',
    value: withDepositFunnelValue(value),
  });
}

/** 充值失败后记时间，供 24h 内 recover */
export function noteDepositFailForRecover() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(userStorageKey(DEPOSIT_FAIL_AT_PREFIX), String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * @param {Record<string, unknown>} [value]
 */
export function tryTrackDepositRecover(value = {}) {
  if (typeof localStorage === 'undefined') return;
  const failKey = userStorageKey(DEPOSIT_FAIL_AT_PREFIX);
  let failAt = 0;
  try {
    failAt = Number(localStorage.getItem(failKey) || 0);
  } catch {
    return;
  }
  if (!Number.isFinite(failAt) || failAt <= 0) return;
  if (Date.now() - failAt > RECOVER_WINDOW_MS) {
    try {
      localStorage.removeItem(failKey);
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    localStorage.removeItem(failKey);
  } catch {
    /* ignore */
  }
  trackAnalyticsLog({
    key: 'deposit.recover',
    label: '失败后恢复充值成功',
    value: withDepositFunnelValue({
      ...value,
      recoverWithinMs: Date.now() - failAt,
    }),
  });
}

/**
 * deposit.success 打完后补 first / recover。
 * @param {Record<string, unknown>} [value]
 */
export function afterDepositSuccessTracked(value = {}) {
  tryTrackDepositSuccessFirst(value);
  tryTrackDepositRecover(value);
  scheduleCustodialBindAfterDepositSuccess();
}

/**
 * 首次入账后开通 Privy：带短退避，避免入账刚写库时 HasRealDeposit 尚未可见。
 * 与埋点解耦入口仍挂在 afterDepositSuccessTracked，保证各充值轨统一触发。
 */
function scheduleCustodialBindAfterDepositSuccess() {
  void (async () => {
    try {
      const m = await import('@/utils/privy-platform-login');
      const delays = [0, 800, 1600, 3200];
      let last = null;
      for (let i = 0; i < delays.length; i += 1) {
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
        }
        last = await m.scheduleSilentPrivyBindAfterFirstDeposit?.();
        if (last?.ok || last?.skipped) {
          const { useCustodialSessionStore } = await import('@/stores/custodialSession');
          useCustodialSessionStore().markReadyAfterBind();
          return;
        }
        if (last?.manualAuth) return;
        if (!last?.deferredUntilDeposit) break;
      }
      const { useCustodialSessionStore } = await import('@/stores/custodialSession');
      void useCustodialSessionStore().ensure({ force: true, maxAttempts: 3 });
    } catch (err) {
      console.warn('[deposit] post-success custodial bind schedule failed', err);
    }
  })();
}

/** deposit.fail 打完后记 recover 窗口 */
export function afterDepositFailTracked() {
  noteDepositFailForRecover();
}

/**
 * @param {Record<string, unknown>} [value]
 */
export function tryTrackTradeSuccessFirst(value = {}) {
  const key = userStorageKey(TRADE_FIRST_PREFIX);
  if (localFlag(key)) return;
  markLocalFlag(key);
  trackAnalyticsLog({
    key: 'trade.success_first',
    label: '首次下单成功',
    value: withTradeFunnelValue(value),
  });
}

/**
 * 登录后新手跳转种类。
 * @param {'trialSim'|'updown5m'|'home'|'soft'|'skip'} kind
 * @param {{ loginCount?: number }} [extra]
 */
export function trackPostLoginOnboarding(kind, extra = {}) {
  const map = {
    trialSim: { key: 'onboarding.trial_sim', label: '登录引导试玩盘' },
    updown5m: { key: 'onboarding.updown', label: '登录引导分钟盘' },
    home: { key: 'onboarding.home', label: '登录引导回首页' },
  };
  const hit = map[kind];
  if (!hit) return;
  trackAnalyticsLog({
    key: hit.key,
    label: hit.label,
    value: withAcquisitionProps({
      loginCount: extra.loginCount,
      kind,
    }),
  });
}

/** 新用户盘口引导开始（每用户一次） */
export function tryTrackOnboardingGuideStart() {
  const key = `${userStorageKey(GUIDE_OUTCOME_PREFIX)}:start`;
  if (localFlag(key)) return;
  markLocalFlag(key);
  trackAnalyticsLog({
    key: 'onboarding.guide_start',
    label: '盘口引导开始',
    value: withAcquisitionProps({}),
  });
}

/**
 * 盘口引导完成或跳过（每用户每种 outcome 一次）。
 * @param {'complete'|'skip'} outcome
 * @param {{ step?: string }} [extra]
 */
export function tryTrackOnboardingGuideOutcome(outcome, extra = {}) {
  const kind = outcome === 'skip' ? 'skip' : 'complete';
  const flagKey = `${userStorageKey(GUIDE_OUTCOME_PREFIX)}:${kind}`;
  if (localFlag(flagKey)) return;
  markLocalFlag(flagKey);
  // 互斥：完成与跳过只保留一种
  const other = kind === 'skip' ? 'complete' : 'skip';
  markLocalFlag(`${userStorageKey(GUIDE_OUTCOME_PREFIX)}:${other}`);
  trackAnalyticsLog({
    key: kind === 'skip' ? 'onboarding.guide_skip' : 'onboarding.guide_complete',
    label: kind === 'skip' ? '盘口引导跳过' : '盘口引导完成',
    value: withAcquisitionProps({
      outcome: kind,
      step: extra.step || undefined,
    }),
  });
}
